import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { fetchProjectReports } from '../api/projectReports';
import type { ProjectReport } from '../api/projectReports';

type SortField = 'project_id' | 'title' | 'customer_name' | 'status' | 'project_start' | 'created_at';
type SortOrder = 'asc' | 'desc';

interface StatusOption {
  value: string;
  label: string;
}

export default function ProjectReportList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  const [reports, setReports] = useState<ProjectReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Debounce the search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchProjectReports({
        page,
        page_size: 20,
        search: debouncedSearch || undefined,
        status: statusFilter,
        sort_by: sortField,
        order: sortOrder,
      });
      setReports(result.items);
      setTotalPages(result.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projectReports.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, sortField, sortOrder, t]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  }

  function sortIndicator(field: SortField) {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  const statusOptions: StatusOption[] = [
    { value: '', label: t('projectReports.allStatuses') },
    { value: 'DRAFT', label: t('projectReports.statusDraft') },
    { value: 'PENDING_APPROVAL', label: t('projectReports.statusPending') },
    { value: 'APPROVED', label: t('projectReports.statusApproved') },
  ];

  function statusBadge(status: string) {
    const classes: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-700',
      PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-700',
    };
    const labels: Record<string, string> = {
      DRAFT: t('projectReports.statusDraft'),
      PENDING_APPROVAL: t('projectReports.statusPending'),
      APPROVED: t('projectReports.statusApproved'),
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classes[status] ?? 'bg-gray-100 text-gray-700'}`}>
        {labels[status] ?? status}
      </span>
    );
  }

  const thClass = 'cursor-pointer select-none hover:text-brand-700';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('projectReports.title')}</h1>
        </div>
        <button
          onClick={() => navigate('/project-reports/new')}
          className="btn-primary text-sm"
        >
          {t('projectReports.newReport')}
        </button>
      </header>

      {/* Search & filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-48 max-w-md">
          <label htmlFor="report-search" className="sr-only">
            {t('projectReports.searchPlaceholder')}
          </label>
          <input
            id="report-search"
            type="search"
            placeholder={t('projectReports.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input w-full"
          />
        </div>
        <div className="w-52">
          <Select<StatusOption>
            options={statusOptions}
            value={statusOptions.find((o) => o.value === (statusFilter ?? '')) ?? statusOptions[0]}
            onChange={(opt) => {
              setStatusFilter(opt?.value || undefined);
              setPage(1);
            }}
            isSearchable={false}
            classNamePrefix="react-select"
          />
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">&#x27F3;</span>
          {t('projectReports.loading')}
        </div>
      )}

      {!loading && !error && (
        <>
          {reports.length === 0 ? (
            <div className="card text-center py-12 text-gray-500 text-sm">
              <p>{t('projectReports.noReports')}</p>
              <p className="mt-1">{t('projectReports.createFirst')}</p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('projectReports.title')}>
                  <thead>
                    <tr>
                      <th scope="col" className={thClass} onClick={() => handleSort('project_id')}>
                        {t('projectReports.detail.projectId')}{sortIndicator('project_id')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('title')}>
                        {t('projectReports.titleCol')}{sortIndicator('title')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('customer_name')}>
                        {t('projectReports.customerCol')}{sortIndicator('customer_name')}
                      </th>
                      <th scope="col">
                        {t('projectReports.sectorsCol')}
                      </th>
                      <th scope="col">
                        {t('projectReports.authorsCol')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('status')}>
                        {t('projectReports.statusCol')}{sortIndicator('status')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('project_start')}>
                        {t('projectReports.startCol')}{sortIndicator('project_start')}
                      </th>
                      <th scope="col" className={thClass} onClick={() => handleSort('created_at')}>
                        {t('projectReports.createdAtCol')}{sortIndicator('created_at')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => (
                      <tr
                        key={report.id}
                        onClick={() => navigate(`/project-reports/${report.id}`)}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="text-gray-500 text-xs font-mono">{report.project_id}</td>
                        <td className="font-medium text-gray-900">{report.title}</td>
                        <td className="text-gray-600 text-xs">{report.customer_name ?? '-'}</td>
                        <td className="text-gray-600 text-xs">
                          {report.sector_names && report.sector_names.length > 0
                            ? report.sector_names.join(', ')
                            : '-'}
                        </td>
                        <td className="text-gray-600 text-xs">{report.author_names || '-'}</td>
                        <td>{statusBadge(report.status)}</td>
                        <td className="text-xs text-gray-500">
                          {report.project_start ? (
                            <time dateTime={report.project_start}>
                              {new Date(report.project_start).toLocaleDateString(dateLocale, {
                                day: '2-digit', month: 'short', year: 'numeric',
                              })}
                            </time>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="text-xs text-gray-500">
                          <time dateTime={report.created_at}>
                            {new Date(report.created_at).toLocaleDateString(dateLocale, {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </time>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="Pagination" className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600">
                {t('common.pageOf', { page, totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                {t('common.next')}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
